import React, { ChangeEvent, FC } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import SVG from '@app/components/SVG';
import ClippySpriteSymbol from '@app/images/clippy-primary.svg';
import PlusSpriteSymbol from '@app/images/plus-primary.svg';
import useAppStores from '@app/hooks/useAppStores';
import SupplierRegistrationMode from '@supplierRegistration/supplierRegistrationMode';
import {
  DOCUMENT_FILE_OPTIONS,
  SupplierDocumentType,
} from '@supplierRegistration/stores/appStores/SupplierRegistrationDocumentsStore';
import DocumentFileRow from '@supplierRegistration/routes/supplierRegistrationProcess/documentUpload/DocumentFileRow';
import {
  AddMoreLabel,
  EmptyAttachLabel,
  FileRowsList,
  FormatsHint,
  Section,
  SectionHelperText,
  SectionTitle,
  VisuallyHiddenFileInput,
} from '@supplierRegistration/routes/supplierRegistrationProcess/documentUpload/DocumentUploadSection.styles';

export interface DocumentUploadSectionProps {
  type: SupplierDocumentType;
  // Returning false blocks the attach click from reaching the wrapped file input (so the native
  // file dialog never opens) -- the caller is expected to handle whatever gates the attach (e.g.
  // opening an email-verification modal) and let the next click through once it's satisfied.
  onBeforeAttach?: () => boolean;
}

const ACCEPT_ATTRIBUTE = DOCUMENT_FILE_OPTIONS.allowedExtensions?.map((extension) => `.${extension}`).join(',');

const ATTACH_INPUT_TEST_ID: Record<SupplierDocumentType, string> = {
  [SupplierDocumentType.bankAccountDocumentation]: 'btn-supp-reg-bank-documents-attach',
  [SupplierDocumentType.taxIdCertificate]: 'btn-supp-reg-tax-documents-attach',
};

/**
 * Renders one document type's upload area (title + helper text + attach/attached UI). The
 * dashed-border "Attach File" box and the small "Add" link each wrap the *same* hidden file
 * input (rendered by the shared renderFileInput() below) in a differently-styled label depending
 * on whether any files are attached yet. The `data-testid` lives on that input itself, not on
 * either label, so it stays addressable under one name (ATTACH_INPUT_TEST_ID / the
 * bankDocumentsAttach / taxDocumentsAttach testId registry entries) regardless of which label is
 * currently rendered around it.
 * Past DOCUMENT_FILE_OPTIONS.maxFilesAllowed, neither label (and so neither input) renders: the
 * store itself never stops accepting more files (see its own comment), this is purely a UI-level
 * cap on the trigger.
 */
const DocumentUploadSection: FC<DocumentUploadSectionProps> = observer((props) => {
  const { type, onBeforeAttach } = props;
  const { t } = useTranslation();
  const { supplierRegistrationDocumentsStore: store } = useAppStores<SupplierRegistrationMode>();

  const files = store.filesByType[type];
  const canAttachMore = files.length < DOCUMENT_FILE_OPTIONS.maxFilesAllowed;
  const inputTestId = ATTACH_INPUT_TEST_ID[type];

  const handleFilesSelected = (e: ChangeEvent<HTMLInputElement>): void => {
    Array.from(e.target.files ?? []).forEach((file) => store.addFile(type, file));
    // Clears the native selection so picking the exact same file again still fires `change`.
    e.target.value = '';
  };

  // A label's default action forwards its click to the file input it wraps (opening the native
  // file dialog) -- preventDefault() here stops that forwarding, so a failing gate blocks the
  // dialog itself rather than letting it open and rejecting the selection afterwards. This also
  // covers keyboard activation: Enter/Space on the focused input dispatches a click that bubbles
  // up through this label before the browser acts on it.
  const handleAttachLabelClick = (e: React.MouseEvent<HTMLLabelElement>): void => {
    if (onBeforeAttach && !onBeforeAttach()) {
      e.preventDefault();
    }
  };

  const handleDelete = (index: number): void => {
    store.removeFile(type, index);
  };

  const renderFileInput = (): React.ReactElement => (
    <VisuallyHiddenFileInput
      type='file'
      accept={ACCEPT_ATTRIBUTE}
      multiple
      data-testid={inputTestId}
      onChange={handleFilesSelected}
    />
  );

  return (
    <Section>
      <SectionTitle>{t(`supplierValidation.supplierRegister.documents.${type}.title`)}</SectionTitle>
      <SectionHelperText>{t(`supplierValidation.supplierRegister.documents.${type}.helperText`)}</SectionHelperText>
      {files.length > 0 && (
        <FileRowsList>
          {files.map((fileAndErrors, index) => (
            <DocumentFileRow
              key={fileAndErrors.rowId}
              type={type}
              index={index}
              fileAndErrors={fileAndErrors}
              onDelete={handleDelete}
            />
          ))}
        </FileRowsList>
      )}
      {files.length === 0 && canAttachMore && (
        <>
          <EmptyAttachLabel onClick={handleAttachLabelClick}>
            <SVG accessibilityLabel='' image={ClippySpriteSymbol} width={18} height={18} />
            {t('supplierValidation.supplierRegister.documents.attachFile')}
            {renderFileInput()}
          </EmptyAttachLabel>
          <FormatsHint>{t('supplierValidation.supplierRegister.documents.formatsHint')}</FormatsHint>
        </>
      )}
      {files.length > 0 && canAttachMore && (
        <AddMoreLabel onClick={handleAttachLabelClick}>
          <SVG accessibilityLabel='' image={PlusSpriteSymbol} width={14} height={14} />
          {t('supplierValidation.supplierRegister.documents.addMore')}
          {renderFileInput()}
        </AddMoreLabel>
      )}
    </Section>
  );
});

export default DocumentUploadSection;
